import isNil from 'lodash/isNil';

import { type CalendarDate, fromCalendarDate, isBefore, isEqual, toCalendarDate } from '../dateutils';
import { InvoiceType, VariousOperationType, type Invoice, type LeaseVariousOperation, type Posting } from '../types';

import { computeSchedulePeriods, findPeriodIndex } from './billingItemCorrectionUtils';
import { BILLING_ITEM_CORRECTION_CUSTOM_VALUE, getInvoiceCustomValue } from './invoiceHelper';

export type LvoCursorRollback =
  | { kind: 'ROLL_BACK'; lastInvoiceDate: string | null }
  | { kind: 'KEEP'; reason: 'NOT_LATEST' | 'NO_MATCHING_PERIOD' };

export const getOwnDocumentPeriodStarts = (
  invoice: Pick<Invoice, 'type' | 'customValues'>,
  postings: Pick<Posting, 'leaseVariousOperationId' | 'periodFrom'>[]
): Map<string, CalendarDate> => {
  const periodStarts = new Map<string, CalendarDate>();
  const isOwnDocument =
    invoice.type === InvoiceType.LEASE_VARIOUS_OPERATION &&
    isNil(getInvoiceCustomValue(invoice, BILLING_ITEM_CORRECTION_CUSTOM_VALUE));
  if (!isOwnDocument) {
    return periodStarts;
  }
  postings.forEach(({ leaseVariousOperationId, periodFrom }) => {
    if (isNil(leaseVariousOperationId)) {
      return;
    }
    const postingStart = toCalendarDate(periodFrom);
    const earliestStart = periodStarts.get(leaseVariousOperationId);
    if (isNil(earliestStart) || isBefore(postingStart, earliestStart)) {
      periodStarts.set(leaseVariousOperationId, postingStart);
    }
  });
  return periodStarts;
};

export const planLvoCursorRollback = (
  operation: Pick<
    LeaseVariousOperation,
    | 'type'
    | 'startDate'
    | 'endDate'
    | 'paymentFrequency'
    | 'paymentInvoicePeriod'
    | 'paymentInvoicePeriodCustomDay'
    | 'lastInvoiceDate'
  >,
  documentPeriodStart: CalendarDate
): LvoCursorRollback => {
  if (isNil(operation.lastInvoiceDate) || !isEqual(toCalendarDate(operation.lastInvoiceDate), documentPeriodStart)) {
    return { kind: 'KEEP', reason: 'NOT_LATEST' };
  }
  if (operation.type === VariousOperationType.ONE_TIME) {
    return { kind: 'ROLL_BACK', lastInvoiceDate: null };
  }
  // The engine starts a period on the cursor, so the cursor that regenerates this period is the previous period's start
  const periods = computeSchedulePeriods(operation);
  const periodIndex = findPeriodIndex(periods, documentPeriodStart);
  if (periodIndex === -1) {
    return { kind: 'KEEP', reason: 'NO_MATCHING_PERIOD' };
  }
  return {
    kind: 'ROLL_BACK',
    lastInvoiceDate: periodIndex === 0 ? null : fromCalendarDate(periods[periodIndex - 1].periodFrom),
  };
};
